import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Breadcrumbs,
  Link,
  Paper,
  Typography,
  Chip,
  CircularProgress
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { usePermissions } from '../../GlobalFunction/userPermissions';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import HomeIcon from '@mui/icons-material/Home';
import moment from 'moment';
import axiosInstance from '../../lib/axios';
import PropertyFilter from '../PropertyFilter';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectProperties,
  selectGlobalProperty,
  selectLocalProperty,
  setLocalProperty,
  fetchProperties
} from '../../redux/propertySlice';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const gridStyles = `
  .ag-theme-alpine {
    --ag-border-color: #c8c8c8;
    --ag-header-background-color: #f8f9fa;
    --ag-odd-row-background-color: #fcfcfc;
    --ag-border-radius: 6px;
  }
  
  .ag-theme-alpine .ag-cell {
    border-right: 1px solid #e0e0e0;
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    align-items: center;
  }
  
  .ag-theme-alpine .ag-header-cell {
    border-right: 1px solid #c8c8c8;
    border-bottom: 1px solid #c8c8c8;
    font-weight: 600;
  }
  
  .ag-theme-alpine .ag-floating-filter-input {
    border-radius: 4px;
  }
  
  .ag-theme-alpine .ag-floating-filter-input:focus {
    outline: none;
    border-color: #3f51b5;
    box-shadow: 0 0 0 2px rgba(63, 81, 181, 0.2);
  }
  
  .ag-theme-alpine .ag-header-cell::after {
    display: none;
  }
  
  .ag-theme-alpine .ag-ltr .ag-cell-first-right-pinned {
    border-left: 1px solid #c8c8c8;
  }
  
  .ag-floating-filter-button{
    margin-left: 0px !important 
  }
  
  .ag-header-cell{
    padding-left: 2px;
    padding-right: 0px;
  }
  
  .truncated-cell {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
  
  .status-chip {
    height: 20px;
    font-size: 0.75rem;
    font-weight: 600;
  }
  
   .ag-input-wrapper:before {
    background-color: white;
    display:none
  }
    .ag-theme-alpine .ag-floating-filter input[type="date"]{
      padding-left: 0 !important;
    }
      :where(.ag-ltr) .ag-header-cell-resize {
    right: -6px !important;
}
`;

const TruncatedCellRenderer = (props) => {
  const [isOverflowed, setIsOverflowed] = useState(false);
  const cellRef = React.useRef(null);

  useEffect(() => {
    if (cellRef.current) {
      const isTextOverflowed =
        cellRef.current.scrollWidth > cellRef.current.clientWidth ||
        (props.value && props.value.length > 20);
      setIsOverflowed(isTextOverflowed);
    }
  }, [props.value]);

  const cellValue = props.valueFormatted ? props.valueFormatted : props.value;

  return (
    <Tooltip
      title={isOverflowed ? cellValue : ''}
      placement="top"
      arrow
      disableHoverListener={!isOverflowed}
    >
      <div
        ref={cellRef}
        className="truncated-cell"
        style={{ width: '100%' }}
      >
        {cellValue}
      </div>
    </Tooltip>
  );
};

const StatusCellRenderer = (props) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'Booked':
        return '#d88c19ff';
      case 'Confirmed':
        return '#85510cff';
      case 'Checked In':
        return '#155717ff';
      case 'CheckOut':
        return '#722542ff';
      case 'Cancel':
        return '#bd1c11ff';
      default:
        return '#757575';
    }
  };

  return (
    <Chip
      label={props.value}
      className="status-chip"
      sx={{
        backgroundColor: getStatusColor(props.value),
        color: 'white',
        width: '100%',
        maxWidth: '100px',
      }}
    />
  );
};

const columnDefs = [
  { headerName: 'Room No', field: 'RoombedName', width: 100, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'Name', field: 'ResidentName', width: 120, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'Mobile', field: 'MobileNo', width: 100, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
  {
    headerName: 'BookDt', field: 'BookDt', width: 110, filter: 'agDateColumnFilter', cellRenderer: TruncatedCellRenderer,
    filterParams: {
      comparator: function (filterLocalDateAtMidnight, cellValue) {
        if (!cellValue) return -1; // empty cells

        // cellValue is in 'DD-MM-YYYY' format
        const [day, month, year] = cellValue.split('-');
        const cellDate = new Date(Number(year), Number(month) - 1, Number(day));

        if (cellDate < filterLocalDateAtMidnight) return -1;
        else if (cellDate > filterLocalDateAtMidnight) return 1;
        else return 0;
      }
    }
  },
  {
    headerName: 'Checkout', field: 'BookDtFrom', width: 110, filter: 'agDateColumnFilter', cellRenderer: TruncatedCellRenderer,
    filterParams: {
      comparator: function (filterLocalDateAtMidnight, cellValue) {
        if (!cellValue) return -1; // empty cells

        // cellValue is in 'DD-MM-YYYY' format
        const [day, month, year] = cellValue.split('-');
        const cellDate = new Date(Number(year), Number(month) - 1, Number(day));

        if (cellDate < filterLocalDateAtMidnight) return -1;
        else if (cellDate > filterLocalDateAtMidnight) return 1;
        else return 0;
      }
    }
  },
  { headerName: 'Duration', field: 'Duration', width: 70, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'Deposit', field: 'DepositeAmt', width: 80, filter: 'agNumberColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'Status', field: 'BookStatus', width: 110, filter: 'agTextColumnFilter', cellRenderer: StatusCellRenderer },
  { headerName: 'BookNo', field: 'BookNo', width: 90, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'Property', field: 'PropName', width: 200, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'Email', field: 'Email', width: 220, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
  {
    headerName: 'BookTo', field: 'BookUpToDt', width: 100, filter: 'agDateColumnFilter', cellRenderer: TruncatedCellRenderer,
    filterParams: {
      comparator: function (filterLocalDateAtMidnight, cellValue) {
        if (!cellValue) return -1; // empty cells

        // cellValue is in 'DD-MM-YYYY' format
        const [day, month, year] = cellValue.split('-');
        const cellDate = new Date(Number(year), Number(month) - 1, Number(day));

        if (cellDate < filterLocalDateAtMidnight) return -1;
        else if (cellDate > filterLocalDateAtMidnight) return 1;
        else return 0;
      }
    }
  },
  { headerName: 'TotalRent', field: 'TotalRent', width: 90, filter: 'agNumberColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'TopUpDays', field: 'Topupdays', width: 80, filter: 'agNumberColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'Net Payable', field: 'AdvcRcble', width: 90, filter: 'agNumberColumnFilter', cellRenderer: TruncatedCellRenderer },
];

const statusOptions = [
  { value: 'B', label: 'Booked' },
  { value: 'C', label: 'Confirmed' },
  { value: 'O', label: 'CheckedIn' },
  { value: 'L', label: 'CheckedOut' },
  { value: 'X', label: 'Cancel' },
];

export default function PlanChangetable() {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerms, setSearchTerms] = useState('');
  const navigate = useNavigate();
  const permissions = usePermissions('Booking');
  const { AddRec = '0' } = permissions || {};

  // const [SelectedProperty, setSelectedProperty] = useState(null);
  const UserId = sessionStorage.getItem('UserId');
  const [selectedStatus, setSelectedStatus] = useState('');
  const dispatch = useDispatch();
  const properties = useSelector(selectProperties);
  const globalProperty = useSelector(selectGlobalProperty);
  const localProperty = useSelector(state => selectLocalProperty(state, 'planChange'));
  const selectedProperty = localProperty || globalProperty;
  const [SelectedProperty, setSelectedProperty] = useState(selectedProperty);
  useEffect(() => {
    dispatch(fetchProperties());
  }, [dispatch]);


  useEffect(() => {
    setSelectedProperty(selectedProperty);
  }, [selectedProperty]);

  const handlePropertyChange = (event) => {
    const selectedId = event.target.value;
    setSelectedProperty(selectedId);
    dispatch(setLocalProperty({
      componentId: 'planChange',
      propertyId: selectedId
    }));
  };

  const [gridApi, setGridApi] = useState(null);
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = gridStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {
    if (SelectedProperty) {
      fetchCompanyData();
    }
  }, [SelectedProperty, selectedStatus]);

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
  };

  const fetchCompanyData = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post('TrnBooking/GetFilterTrnBookingDashBoard', {
        start: 0,
        PageSize: 0,
        SearchText: '',
        PropId: SelectedProperty || 0,
        Flag: selectedStatus
      });

      if (response.data.Status === 0) {
        const formattedRows = response.data.Data.map((obj) => ({
          ...obj,
          BookDt: obj.BookDt ? moment(obj.BookDt).format('DD-MM-YYYY') : '',
          BookDtFrom: obj.BookDtFrom ? moment(obj.BookDtFrom).format('DD-MM-YYYY') : '',
          CheckoutDt: obj.CheckoutDt ? moment(obj.CheckoutDt).format('DD-MM-YYYY') : '',
          BookUpToDt: obj.BookUpToDt ? moment(obj.BookUpToDt).format('DD-MM-YYYY') : '',
          BookSt: obj.BookSt,
          BookStatus: obj.BookStatus
        }));
        setRows(formattedRows);

        if (gridApi) {
          setTimeout(() => {
            gridApi?.sizeColumnsToFit();
          }, 0);
        }
      } else {
        setRows([]);
      }
    } catch (error) {
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  const ClearFilter = useCallback(() => {
    if (gridApi) {
      gridApi.setFilterModel(null);
    }
    setSelectedStatus('');
  }, [gridApi]);

  const handlePermittedClick = () => {
    if (AddRec === '1') {
      navigate('/booking');
    }
  };

  const handleHomeClick = () => {
    navigate('/dashboard');
  };

  const handleRowDoubleClick = useCallback((params) => {
    navigate('/planchange', { state: { BookId: params.data.BookId, mode: 'view' } });
  }, [navigate]);

  const handleLocationclick = () => {
    navigate('/enquiry/pre-registration');
  };

  const addButtonStyles = {
    background: AddRec === '1' ? '#635bff' : 'rgba(0, 0, 0, 0.12)',
    color: AddRec === '1' ? 'white' : 'rgba(0, 0, 0, 0.26)',
    borderRadius: '50px',
    padding: '5px 15px',
    boxShadow: AddRec === '1' ? '0px 6px 15px rgba(0, 0, 0, 0.3)' : 'none',
    transition: 'background 0.3s ease',
    '&:hover': {
      background: AddRec === '1' ? '#524ccc' : 'rgba(0, 0, 0, 0.12)',
    },
    '&:disabled': {
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
      color: 'rgba(0, 0, 0, 0.26)',
    },
  };

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: true,
    filterParams: {
      suppressAndOrCondition: true,
    },
    cellStyle: {
      borderRight: '1px solid #e0e0e0',
      display: 'flex',
      alignItems: 'center'
    },
    headerClass: 'ag-header-cell',
    suppressSizeToFit: true,
  }), []);

  const PropertyFilterJSX = (
    <FormControl size="small" style={{ width: '250px' }}>
      <InputLabel id="property-select-label">Filter by Property</InputLabel>
      <Select
        labelId="property-select-label"
        id="property-select"
        value={SelectedProperty || ''}
        onChange={handlePropertyChange}
        label="Filter by Property"
      >
        {properties.map((property) => (
          <MenuItem key={property.Id} value={property.Id}>
            {property.Name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box sx={{ width: '100%', height: '90vh', display: 'flex', flexDirection: 'column', p: 2, background: 'rgb(249, 249, 249)' }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        flexWrap: 'wrap',
        rowGap: 2,
        background: 'rgb(255, 255, 255)',
        padding: '12px',
        borderRadius: '5px',
        boxShadow: 'rgb(0 0 0 / 8%)'
      }}>
        <Breadcrumbs
          aria-label="breadcrumb"
          separator="›"
          sx={{
            fontSize: { xs: "14px", sm: "16px" },
            display: "flex",
            flexDirection: { xs: "column", sm: "column", md: "row" }
          }}
        >
          <Link
            underline="hover"
            color="#635bff"
            sx={{ cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center" }}
            onClick={handleHomeClick}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            <Box component="span">Home</Box>
          </Link>

          <Link
            underline="hover"
            color="#635bff"
            sx={{ cursor: "pointer", fontWeight: "500" }}
            onClick={handleLocationclick}
          >
            Booking(Pre-Registration)
          </Link>

          <Typography color="text.primary" sx={{ fontWeight: "500" }}>
            Plan Change
          </Typography>
        </Breadcrumbs>

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          rowGap: 2
        }}>
          {PropertyFilterJSX}
          <FormControl sx={{ minWidth: 250 }} size="small">
            <InputLabel id="status-filter-label">Booking Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={selectedStatus}
              onChange={handleStatusChange}
              label="Booking Status"
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title="Clear all filters" arrow>
            <span>
              <Button
                variant="outlined"
                onClick={ClearFilter}
                size="small"
                sx={{
                  borderRadius: "50px",
                  textTransform: "none",
                  borderColor: "#635bff",
                  color: "#635bff",
                  "&:hover": {
                    borderColor: "#524ccc",
                    backgroundColor: "rgba(99, 91, 255, 0.04)",
                  }
                }}
              >
                Clear Filter
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={AddRec !== '1' ? 'You have no permission to add record' : 'Add New Record'} placement="top" arrow>
            <span>
              <Button
                variant="contained"
                onClick={handlePermittedClick}
                size="small"
                disabled={AddRec !== '1'}
                sx={addButtonStyles}
              >
                <AddIcon /> New
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Paper
        elevation={2}
        sx={{
          height: 'calc(100vh - 250px)',
          width: '100%',
          flexGrow: 1,
          overflow: 'hidden'
        }}
      >
        <Box
          className="ag-theme-alpine"
          style={{
            height: '100%',
            width: '100%',
            overflowX: 'auto'
          }}
        >
          {isLoading ? (
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              textAlign: 'center',
            }}
            >
              <CircularProgress size='3rem' sx={{ color: '#635bff' }} />
              <Typography sx={{ marginTop: 2, color: '#635bff', fontWeight: 'bold' }}>
                Loading Data...
              </Typography>
            </Box>
          ) : (
            <AgGridReact
              columnDefs={columnDefs}
              rowData={rows}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              onRowDoubleClicked={handleRowDoubleClick}
              pagination={true}
              paginationPageSize={1000}
              paginationPageSizeSelector={[50, 100, 500, 1000, 5000]}
              suppressRowClickSelection={true}
              rowSelection={'single'}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              loading={isLoading}
              rowHeight={30}
              headerHeight={30}
              floatingFiltersHeight={28}
              overlayLoadingTemplate={
                '<span class="ag-overlay-loading-center">Loading data...</span>'
              }
              overlayNoRowsTemplate={
                '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;">No data found</span>'
              }
              suppressMenuHide={true}
              enableRangeSelection={true}
              sideBar={false}
              suppressHorizontalScroll={false}
            />
          )}
        </Box>
      </Paper>
      <Typography variant="body2" sx={{ fontWeight: 'medium', position: 'absolute', bottom: '35px', left: '90px' }}>
        Total Records: {rows.length}
      </Typography>
    </Box>
  );
}